import * as React from "react"
import { Pencil, PowerOff } from "lucide-react"
import type { Account } from "@/types"
import { Badge } from "@/shared/ui/atoms/Badge"
import { Button } from "@/shared/ui/atoms/Button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/atoms/Table"
import { EmptyCollection } from "../atoms/EmptyCollection"
import { formatTransactionDate } from "../../utils/formatters"
import { formatCurrency } from "@/lib/utils/formatCurrency"
import { useIsMobile } from "@/shared/hooks/useMediaQuery"

interface AccountCollectionProps {
  accounts: Account[]
  amountsVisible?: boolean
  onEdit?: (account: Account) => void
  onDelete?: (account: Account) => void
}

export const AccountCollection = React.memo(function AccountCollection({
  accounts,
  amountsVisible = true,
  onEdit,
  onDelete,
}: AccountCollectionProps) {
  const isMobile = useIsMobile()

  if (accounts.length === 0) {
    return <EmptyCollection>No hay cuentas todavía. Crea la primera para empezar.</EmptyCollection>
  }

  if (isMobile) {
    return (
      /* Mobile Touch-Friendly Card List */
      <div className="flex flex-col divide-y divide-border/60">
        {accounts.map((account) => (
          <div key={account.id} className="flex items-center justify-between p-3.5 hover:bg-muted/40 transition-colors">
            <div className="flex flex-col gap-1 min-w-0 pr-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-medium text-sm truncate ${account.is_active === false ? "text-muted-foreground line-through" : "text-foreground"}`}>
                  {account.name}
                </span>
                {account.is_active === false && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                    Inactiva
                  </Badge>
                )}
              </div>
              <span className="text-[11px] text-muted-foreground">{formatTransactionDate(account.created_at)}</span>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <div className="flex items-center gap-1.5">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {account.is_savings_account ? "Ahorro" : "Disponible"}
                </Badge>
                <span className="font-mono text-[11px] font-semibold px-1.5 py-0 rounded bg-muted text-foreground">
                  {account.currency}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold text-xs text-foreground">
                  {amountsVisible
                    ? formatCurrency(Number(account.current_balance || 0), account.currency)
                    : "••••••"}
                </span>
                {(onEdit || onDelete) && (
                  <div className="flex items-center gap-0.5 ml-1">
                    {onEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-foreground"
                        onClick={() => onEdit(account)}
                        aria-label={`Editar cuenta ${account.name}`}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    )}
                    {onDelete && account.is_active !== false && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        onClick={() => onDelete(account)}
                        aria-label={`Desactivar cuenta ${account.name}`}
                      >
                        <PowerOff className="size-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    /* Desktop / Tablet Table */
    <div className="overflow-x-auto">
      <Table className="cartera-table">
        <TableHeader>
          <TableRow>
            <TableHead>Cuenta</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Moneda</TableHead>
            <TableHead>Creada</TableHead>
            <TableHead className="text-right">Saldo Total</TableHead>
            {(onEdit || onDelete) && <TableHead className="w-[84px] text-right">Acciones</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.map((account) => (
            <TableRow key={account.id} className="cartera-table-row">
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <span className={account.is_active === false ? "text-muted-foreground line-through" : ""}>
                    {account.name}
                  </span>
                  {account.is_active === false && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                      Inactiva
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{account.is_savings_account ? "Ahorro" : "Disponible"}</Badge>
              </TableCell>
              <TableCell className="font-mono text-xs font-medium tracking-wide">{account.currency}</TableCell>
              <TableCell className="text-muted-foreground">{formatTransactionDate(account.created_at)}</TableCell>
              <TableCell className="cartera-amount text-right font-mono font-semibold text-sm whitespace-nowrap text-foreground">
                {amountsVisible
                  ? formatCurrency(Number(account.current_balance || 0), account.currency)
                  : "••••••"}
              </TableCell>
              {(onEdit || onDelete) && (
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {onEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-foreground"
                        onClick={() => onEdit(account)}
                        aria-label={`Editar cuenta ${account.name}`}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    )}
                    {onDelete && account.is_active !== false && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        onClick={() => onDelete(account)}
                        aria-label={`Desactivar cuenta ${account.name}`}
                      >
                        <PowerOff className="size-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
})



